import { useEffect, useState } from 'react'
import { Alert, Anchor, Button, Container, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { auth } from '@chattee.ai/auth'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import backend from '../../backend/backend'
import Notify from '../../components/shared/Notify'
import Message from '../../i18n/Message'
import { describeError, useT } from '../../hooks'

/** First owner bootstrap. Only reachable while no super admin exists. */
export function Setup() {
  const t = useT()
  const navigate = useNavigate()
  const [needs, setNeeds] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const form = useForm({
    initialValues: { code: '', name: '', email: '', password: '' },
    validate: {
      code: (v) => (v ? null : t('error_required')),
      name: (v) => (v.trim() ? null : t('error_required')),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : t('error_email')),
      password: (v) => (v.length >= 12 ? null : t('error_password_length')),
    },
  })
  useEffect(() => {
    backend.getSetupStatus().then((s) => setNeeds(!!s.needsOwnerSetup)).catch(() => setNeeds(false))
  }, [])
  if (needs === false) return <Navigate to="/login" replace />
  if (needs === null) return null

  const submit = async (values) => {
    setBusy(true)
    setError(null)
    try {
      await backend.setupOwner(values)
      Notify.success(t('setup_done'))
      navigate('/login')
    } catch (err) {
      setError(err?.code === 'setup_code_not_configured' ? t('setup_code_missing') : describeError(t, err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Container size={440} py={60}>
      <Stack>
        <Title order={2}>
          <Message id="setup_title" />
        </Title>
        <Text c="dimmed">
          <Message id="setup_hint" />
        </Text>
        {error && <Alert color="red">{error}</Alert>}
        <form onSubmit={form.onSubmit(submit)} noValidate>
          <Stack>
            <PasswordInput label={t('setup_code')} {...form.getInputProps('code')} />
            <TextInput label={t('field_name')} {...form.getInputProps('name')} />
            <TextInput label={t('field_email')} type="email" {...form.getInputProps('email')} />
            <PasswordInput label={t('field_password')} autoComplete="new-password" {...form.getInputProps('password')} />
            <Button type="submit" loading={busy}>
              <Message id="setup_submit" />
            </Button>
          </Stack>
        </form>
        <Anchor component={Link} to="/login" size="sm">
          <Message id="back_to_login" />
        </Anchor>
      </Stack>
    </Container>
  )
}

/** Only usable when the platform reports an email provider is connected. */
export function ForgotPassword() {
  const t = useT()
  const [available, setAvailable] = useState(null)
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  useEffect(() => {
    auth.getConfig().then((c) => setAvailable(!!c.emailAvailable)).catch(() => setAvailable(false))
  }, [])
  if (available === null) return null
  if (!available) return <Navigate to="/login" replace />
  return (
    <Container size={420} py={80}>
      <Stack>
        <Title order={2}>
          <Message id="forgot_title" />
        </Title>
        {sent ? (
          <Alert color="green">
            <Message id="forgot_sent" />
          </Alert>
        ) : (
          <>
            <TextInput label={t('field_email')} type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
            <Button
              disabled={!email.trim()}
              onClick={async () => {
                await auth.requestPasswordReset({ email: email.trim() }).catch(() => {})
                setSent(true)
              }}
            >
              <Message id="forgot_submit" />
            </Button>
          </>
        )}
        <Anchor component={Link} to="/login" size="sm">
          <Message id="back_to_login" />
        </Anchor>
      </Stack>
    </Container>
  )
}

export function ResetPassword() {
  const t = useT()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  return (
    <Container size={420} py={80}>
      <Stack>
        <Title order={2}>
          <Message id="reset_title" />
        </Title>
        {error && <Alert color="red"><Message id="reset_failed" /></Alert>}
        <PasswordInput label={t('field_password')} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
        <Button
          disabled={password.length < 12}
          onClick={async () => {
            try {
              await auth.confirmPasswordReset({ token: params.get('token'), password })
              Notify.success(t('reset_done'))
              navigate('/login')
            } catch {
              setError(true)
            }
          }}
        >
          <Message id="reset_submit" />
        </Button>
      </Stack>
    </Container>
  )
}
